import { View, Text, TextInput, Pressable, InteractionManager } from 'react-native'
import React, { useEffect, useState, useRef, useCallback, useMemo, memo } from 'react'
import { Query } from '@/utils/react-query/Queries';
import { LegendList } from '@legendapp/list';
import { StyleSheet } from 'react-native-unistyles';
import { useRouter, useFocusEffect } from 'expo-router';
import usePlatform from '@/utils/hooks/usePlatform';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { allowedSearchTypes } from '@/utils/organizationEntitlements';
import Unicon from '@/components/General/Unicon';
import Avatar from '@/components/People/Avatar';
import Animated, { 
    FadeIn, 
    FadeOut, 
    FadeInDown, 
    Layout,
    useAnimatedStyle,
    useSharedValue,
    withRepeat,
    withTiming,
    withSequence,
    Easing
} from 'react-native-reanimated';

const SEARCH_TYPES = ["people", "roles", "tasks", "events", "training", "groups", "paths", "boards", "posts", "files"];
// Display labels for search types whose backend key differs from the UI name
// (universal_search returns 'paths'; canonical module name is Automations).
const TYPE_LABELS = { paths: 'Automations' };
const getTypeLabel = (type) => TYPE_LABELS[type] || type.charAt(0).toUpperCase() + type.slice(1);
const TYPE_ICONS = {
    people: "people-outline",
    roles: "shield-outline",
    tasks: "checkbox-outline", 
    events: "calendar-outline",
    training: "school-outline",
    groups: "people-circle-outline",
    paths: "git-branch-outline",
    boards: "chatbubbles-outline",
    posts: "document-text-outline",
    files: "document-outline"
};

let persistedSearchState = {
    searchText: '',
    debouncedSearchText: '',
    searchTypes: SEARCH_TYPES.filter(t => t === 'people'),
    matchAll: true,
    partialMatch: true,
};

const SkeletonLoader = memo(() => {
    const opacity = useSharedValue(0.3);
    
    useEffect(() => {
        opacity.value = withRepeat(
            withSequence(
                withTiming(0.7, { duration: 800, easing: Easing.inOut(Easing.ease) }),
                withTiming(0.3, { duration: 800, easing: Easing.inOut(Easing.ease) })
            ),
            -1,
            false
        );
    }, []);
    
    const animatedStyle = useAnimatedStyle(() => ({
        opacity: opacity.value,
    }));
    
    return (
        <View style={styles.skeletonContainer}>
            {[1, 2, 3, 4].map((i) => (
                <Animated.View key={i} style={[styles.skeletonItem, animatedStyle]}>
                    <View style={styles.skeletonTitle} />
                    <View style={styles.skeletonSubtitle} />
                </Animated.View>
            ))}
        </View>
    );
});

const TypePill = memo(({ type, isSelected, count, disabled, onPress }) => {
    return (
        <Pressable 
            disabled={disabled} 
            onPress={onPress}
            style={({pressed}) => styles.typePill({disabled, pressed, isSelected})}
        >
            <Unicon 
                name={TYPE_ICONS[type]} 
                size={16} 
                colorVariant={isSelected ? "onPrimary" : "textSecondary"} 
            />
            <Text style={styles.typePillText({isSelected})}>
                {getTypeLabel(type)}
            </Text>
            <View style={styles.typePillCount({isSelected})}>
                <Text style={styles.typePillCountText({isSelected})}>
                    {count}
                </Text>
            </View>
        </Pressable>
    );
});

const SearchResultItem = memo(({ item, type, onPress }) => {
    return (
        <Animated.View 
            entering={FadeInDown.duration(200)} 
            layout={Layout.springify()}
        >
            <Pressable
                onPress={onPress}
                style={({pressed}) => styles.searchResultItem({pressed})}
            >
                {type === 'people' ? (
                    <Avatar profile={item} size={40} disableBorder />
                ) : (
                    <View style={styles.searchResultIcon}>
                        <Unicon name={TYPE_ICONS[type]} size={20} colorVariant="primary" />
                    </View>
                )}
                <View style={styles.searchResultContent}>
                    <Text style={styles.searchResultTitle} numberOfLines={1}>
                        {item.name}
                    </Text>
                    {item.description ? (
                        <Text style={styles.searchResultDescription} numberOfLines={2}>
                            {item.description}
                        </Text>
                    ) : null}
                </View>
                <Unicon name="chevron-forward" size={18} colorVariant="textSecondary" />
            </Pressable>
        </Animated.View>
    );
});

const FilterChip = memo(({ label, isSelected, onPress }) => {
    return (
        <Pressable
            onPress={onPress}
            style={({pressed}) => styles.filterChip({isSelected, pressed})}
        >
            <Text style={styles.filterChipText({isSelected})}>
                {label}
            </Text>
        </Pressable>
    );
});

export default function SearchModal({onSelect, isOpen}) {
    const [searchText, setSearchText] = useState(() => persistedSearchState.searchText);
    const [debouncedSearchText, setDebouncedSearchText] = useState(() => persistedSearchState.debouncedSearchText);
    const [matchAll, setMatchAll] = useState(() => persistedSearchState.matchAll);
    const [partialMatch, setPartialMatch] = useState(() => persistedSearchState.partialMatch);
    const [selectedType, setSelectedType] = useState(null);
    const [searchTypes, setSearchTypes] = useState(() => persistedSearchState.searchTypes);
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        persistedSearchState = { searchText, debouncedSearchText, searchTypes, matchAll, partialMatch };
    }, [searchText, debouncedSearchText, searchTypes, matchAll, partialMatch]);

    const {isWeb, isNative} = usePlatform();
    const router = useRouter();
    const searchInputRef = useRef(null);
    const debounceRef = useRef(null);
    const { entitlements } = useOrganizationEntitlements();

    // Plan-filtered type list — Free orgs search people only. The persisted
    // module selection is sanitized at the effective level (not rewritten) so
    // paid picks survive a downgrade/re-upgrade cycle.
    const allowedTypes = useMemo(() => allowedSearchTypes(entitlements), [entitlements]);
    const effectiveSearchTypes = useMemo(() => {
        const allowed = new Set(allowedTypes);
        const effective = (searchTypes || []).filter((t) => allowed.has(t));
        return effective.length > 0 ? effective : allowedTypes;
    }, [searchTypes, allowedTypes]);

    const { data: searchResults, isFetching: isLoading } = Query.global.all({
        search_term: debouncedSearchText,
        types: effectiveSearchTypes,
        match_all: matchAll,
        partial: partialMatch,
        options: { enabled: debouncedSearchText?.length > 0 }
    });

    useFocusEffect(
        useCallback(() => {
            const interactionPromise = InteractionManager.runAfterInteractions(() => {
                const timer = setTimeout(() => {
                    searchInputRef.current?.focus();
                }, 150);
                return () => clearTimeout(timer);
            });
            return () => interactionPromise.cancel?.();
        }, [])
    );

    useEffect(() => {
        if (!isOpen) return;
        const timer = setTimeout(() => {
            searchInputRef.current?.focus();
        }, 100);
        return () => clearTimeout(timer);
    }, [isOpen]);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setDebouncedSearchText(searchText);
        }, 400);
        return () => clearTimeout(debounceRef.current);
    }, [searchText]);

    useEffect(() => {
        if (searchResults) {
            const firstWithResults = Object.entries(searchResults).find(([_, value]) => value?.length > 0);
            if (firstWithResults) {
                setSelectedType(firstWithResults[0]);
            }
        }
    }, [searchResults]);

    const handleSearch = useCallback((text) => {
        setSearchText(text);
    }, []);

    const handleClear = useCallback(() => {
        setSearchText('');
        setDebouncedSearchText('');
        searchInputRef.current?.focus();
    }, []);

    const handleItemPress = useCallback((item, type) => {
        switch(type) {
            case "people": router.push({pathname: "/people/" + item?.id}); break;
            case "roles": router.push({pathname: "/roles/" + item?.id}); break;
            case 'tasks': router.push({pathname: "/tasks/" + item?.id}); break;
            case 'events': router.push({pathname: "/events/" + item?.id}); break;
            case 'training': router.push({pathname: "/training/" + item?.id}); break;
            case 'groups': router.push({pathname: "/smallgroups/" + item?.id}); break;
            case 'paths': router.push({pathname: "/automations/" + item?.id}); break;
            case 'boards':
                router.push({pathname: `connect/boards/${item.id}/feed`});
                break;
            case 'posts': router.push({pathname: `connect/boards/${item.boardID}/posts/${item.id}`}); break;
            case 'files':
                router.push({
                    pathname: '/files',
                    params: {
                        scope: item.scope || 'resources',
                        ownerID: item.ownerID || '',
                        folderID: item.folderID || 'root'
                    }
                });
                break;
        }
        onSelect?.(item);
    }, [router, onSelect]);

    const toggleSearchType = useCallback((type) => {
        setSearchTypes(current => 
            current.includes(type) 
                ? current.filter(t => t !== type)
                : [...current, type]
        );
    }, []);

    const currentData = useMemo(() => {
        if (!searchResults || !selectedType) return [];
        return searchResults[selectedType] || [];
    }, [searchResults, selectedType]);

    const totalResults = useMemo(() => {
        if (!searchResults) return 0;
        return Object.values(searchResults).reduce((sum, arr) => sum + (arr?.length || 0), 0);
    }, [searchResults]);

    const searchTypesLabel = useMemo(() => {
        if (effectiveSearchTypes.length === 0) return 'select at least one type';
        if (effectiveSearchTypes.length === SEARCH_TYPES.length) return 'anything';
        return effectiveSearchTypes.map(getTypeLabel).join(', ');
    }, [effectiveSearchTypes]);

    const renderItem = useCallback(({item}) => (
        <SearchResultItem 
            item={item} 
            type={selectedType}
            onPress={() => handleItemPress(item, selectedType)}
        />
    ), [selectedType, handleItemPress]);

    const keyExtractor = useCallback((item) => item.id, []);

    const ListEmptyComponent = useMemo(() => (
        <Animated.View entering={FadeIn.duration(300)} style={styles.listEmptyContainer}>
            <Unicon name="search-outline" size={48} colorVariant="textSecondary" />
            <Text style={styles.listEmptyText}>
                No results found
            </Text>
        </Animated.View>
    ), []);

    return (
        <View style={styles.container}>
            <View style={styles.searchContainer}>
                <View style={styles.searchInputWrapper}>
                    <Unicon name="search-outline" size={20} colorVariant="textSecondary" style={styles.searchIcon} />
                    <TextInput
                        testID="SearchBox-UniversalSearch"
                        ref={searchInputRef}
                        returnKeyType='done'
                        value={searchText}
                        onChangeText={handleSearch}
                        placeholder={`Search ${isWeb ? '(press Enter)' : ''}...`}
                        placeholderTextColor={styles.searchInput.placeholderColor}
                        style={styles.searchInput}
                        autoCorrect={false}
                        autoCapitalize="none"
                        onSubmitEditing={() => {
                            setDebouncedSearchText(searchText);
                        }}
                    />
                    {searchText.length > 0 && (
                        <Pressable onPress={handleClear} style={styles.clearButton} hitSlop={20}>
                            <Unicon name="close" size={18} colorVariant="textSecondary" />
                        </Pressable>
                    )}
                </View>
                
                <Pressable 
                    onPress={() => setShowFilters(prev => !prev)}
                    style={({pressed}) => [styles.filterToggle, {opacity: pressed ? 0.7 : 1}]}
                >
                    <Unicon 
                        name={showFilters ? "options" : "options-outline"} 
                        size={22} 
                        colorVariant={showFilters ? "primary" : "textSecondary"} 
                    />
                </Pressable>
            </View>

            {showFilters && (
                <Animated.View 
                    entering={FadeInDown.duration(200)} 
                    exiting={FadeOut.duration(150)}
                    style={styles.filtersContainer}
                >
                    <View style={styles.filterSection}>
                        <View style={styles.filterHeader}>
                            <Text style={styles.filterLabel}>Search in:</Text>
                            <View style={styles.filterActions}>
                                <Pressable onPress={() => setSearchTypes([...allowedTypes])}>
                                    <Text style={styles.filterAction}>All</Text>
                                </Pressable>
                                <Text style={styles.filterActionDivider}> · </Text>
                                <Pressable onPress={() => setSearchTypes([])}>
                                    <Text style={styles.filterAction}>None</Text>
                                </Pressable>
                            </View>
                        </View>
                        <View style={styles.filterChips}>
                            {allowedTypes.map(type => (
                                <FilterChip
                                    key={type}
                                    label={getTypeLabel(type)}
                                    isSelected={effectiveSearchTypes.includes(type)}
                                    onPress={() => toggleSearchType(type)}
                                />
                            ))}
                        </View>
                    </View>
                    
                    <View style={styles.filterSection}>
                        <Text style={styles.filterLabel}>Options:</Text>
                        <View style={styles.filterChips}>
                            <FilterChip
                                label="Match All Words"
                                isSelected={matchAll}
                                onPress={() => setMatchAll(true)}
                            />
                            <FilterChip
                                label="Match Any Word"
                                isSelected={!matchAll}
                                onPress={() => setMatchAll(false)}
                            />
                            <FilterChip
                                label="Partial Match"
                                isSelected={partialMatch}
                                onPress={() => setPartialMatch(!partialMatch)}
                            />
                        </View>
                    </View>
                </Animated.View>
            )}

            {isLoading && debouncedSearchText.length > 0 && (
                <Animated.View entering={FadeIn.duration(200)} exiting={FadeOut.duration(150)}>
                    <SkeletonLoader />
                </Animated.View>
            )}

            {!isLoading && !debouncedSearchText && (
                <Pressable onPress={() => searchInputRef.current?.blur()} style={styles.emptyStateWrapper}>
                    <Animated.View entering={FadeIn.duration(300)} style={styles.emptyState}>
                        <Unicon name="search" size={64} colorVariant="textSecondary" />
                        <Text style={styles.emptyStateTitle}>Search Anything</Text>
                        <Text style={styles.emptyStateSubtitle}>
                            {`Type something and press ${isWeb ? 'Enter' : 'search'} to find ${searchTypesLabel}, or modify your filters to search other types`}
                        </Text>
                    </Animated.View>
                </Pressable>
            )}

            {!isLoading && searchResults && (
                <Animated.View entering={FadeIn.duration(200)} style={styles.resultsContainer}>
                    {totalResults > 0 && (
                        <View style={styles.resultsHeader}>
                            <Text style={styles.resultsCount}>
                                For performance, only the top 15 results for each type are shown
                            </Text>
                        </View>
                    )}
                    
                    <View style={styles.typePills}>
                        {effectiveSearchTypes.map((type) => (
                            <TypePill
                                key={type}
                                type={type}
                                isSelected={selectedType === type}
                                count={searchResults[type]?.length || 0}
                                disabled={!searchResults[type] || searchResults[type]?.length === 0}
                                onPress={() => setSelectedType(type)}
                            />
                        ))}
                    </View>

                    <View style={styles.listContainer}>
                        <LegendList
                            keyboardShouldPersistTaps="handled"
                            keyboardDismissMode={isNative ? "on-drag" : "none"}
                            recycleItems
                            keyExtractor={keyExtractor}
                            estimatedItemSize={72}
                            data={currentData}
                            renderItem={renderItem}
                            ListEmptyComponent={ListEmptyComponent}
                            showsVerticalScrollIndicator={false}
                        />
                    </View>
                </Animated.View>
            )}
        </View>
    );
}


const styles = StyleSheet.create(({colors, fonts, scheme, shadows}, rt) => ({
    container: {
        flex: 1,
        minHeight: rt.isLandscape ? 500 : undefined,
    },
    searchContainer: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
    },
    searchInputWrapper: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: scheme === 'dark' ? colors.surface : colors.background,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: colors.border,
        paddingHorizontal: 12,
        height: 44,
    },
    searchIcon: {
        marginRight: 8,
    },
    searchInput: {
        flex: 1,
        fontSize: 16,
        color: colors.text,
        fontFamily: fonts.body,
        height: '100%',
        outlineStyle: 'none',
        placeholderColor: colors.textSecondary,
    },
    searchResultItem: ({pressed}) => ({
        padding: 14,
        marginVertical: 4,
        backgroundColor: pressed ? colors.border : colors.surface,
        borderRadius: 12,
        boxShadow: shadows.card,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    }),
    searchResultIcon: {
        width: 40,
        height: 40,
        borderRadius: 10,
        backgroundColor: colors.primary + '15',
        alignItems: 'center',
        justifyContent: 'center',
    },
    searchResultContent: {
        flex: 1,
    },
    searchResultTitle: {
        color: colors.text, 
        fontSize: 15, 
        fontFamily: fonts.bold,
    },
    searchResultDescription: {
        color: colors.textSecondary,
        fontSize: 13,
        marginTop: 2,
    },
    skeletonContainer: {
        paddingTop: 20, 
        gap: 12,
    },
    skeletonItem: {
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 16,
        gap: 8,
    },
    skeletonTitle: {
        height: 16, 
        width: '60%', 
        backgroundColor: colors.border, 
        borderRadius: 4,
    },
    skeletonSubtitle: {
        height: 12, 
        width: '90%', 
        backgroundColor: colors.border, 
        borderRadius: 4,
    },
    listEmptyContainer: {
        paddingTop: 40, 
        alignItems: 'center',
    },
    listEmptyText: {
        paddingTop: 12,
        fontFamily: fonts.body, 
        fontSize: 16, 
        textAlign: 'center', 
        color: colors.textSecondary,
    },
    clearButton: {
        padding: 4,
    },
    filterToggle: {
        width: 44,
        height: 44,
        borderRadius: 12,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
        alignItems: 'center',
        justifyContent: 'center',
    },
    filtersContainer: {
        marginTop: 12,
        padding: 12,
        backgroundColor: colors.surface,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: colors.border,
        gap: 12,
    },
    filterSection: {
        gap: 8,
    },
    filterHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    filterLabel: {
        fontSize: 12,
        color: colors.textSecondary,
        fontFamily: fonts.bold,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    filterActions: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    filterAction: {
        fontSize: 12,
        fontFamily: fonts.bold,
        color: colors.primary,
    },
    filterActionDivider: {
        fontSize: 12,
        color: colors.textSecondary,
        fontFamily: fonts.body,
    },
    filterChips: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    emptyStateWrapper: {
        flex: 1,
    },
    emptyState: {
        flex: 1,
        alignItems: 'center',
        // justifyContent: 'center',
        paddingTop: 60,
        paddingHorizontal: 20,
    },
    emptyStateTitle: {
        fontSize: 20,
        fontFamily: fonts.bold,
        color: colors.text,
        marginTop: 16,
    },
    emptyStateSubtitle: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
        marginTop: 8,
        lineHeight: 20,
    },
    resultsContainer: {
        flex: 1,
        marginTop: 16,
    },
    resultsHeader: {
        marginBottom: 12,
    },
    resultsCount: {
        fontSize: 13,
        color: colors.textSecondary,
        fontFamily: fonts.body,
    },
    typePills: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: 16,
    },
    listContainer: {
        flex: 1,
    },
    typePill: ({disabled, pressed, isSelected}) => ({
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        borderRadius: 20,
        paddingVertical: 8,
        paddingHorizontal: 14,
        borderWidth: 1,
        opacity: disabled ? 0.4 : pressed ? 0.8 : 1,
        backgroundColor: isSelected ? colors.primary : colors.surface,
        borderColor: isSelected ? colors.primary : colors.border,
    }),
    typePillText: ({isSelected}) => ({
        fontFamily: fonts.bold, 
        fontSize: 13,
        color: isSelected ? colors.onPrimary : colors.text,
    }),
    typePillCount: ({isSelected}) => ({
        borderRadius: 10,
        paddingHorizontal: 6,
        paddingVertical: 2,
        minWidth: 22,
        alignItems: 'center',
        backgroundColor: isSelected ? 'rgba(255,255,255,0.25)' : colors.border,
    }),
    typePillCountText: ({isSelected}) => ({
        fontFamily: fonts.bold,
        fontSize: 11,
        color: isSelected ? colors.onPrimary : colors.text,
    }),
    filterChip: ({isSelected, pressed}) => ({
        paddingVertical: 6,
        paddingHorizontal: 12,
        borderRadius: 16,
        backgroundColor: isSelected ? colors.primary : 'transparent',
        borderWidth: 1,
        borderColor: isSelected ? colors.primary : colors.border,
        opacity: pressed ? 0.7 : 1,
    }),
    filterChipText: ({isSelected}) => ({
        fontFamily: fonts.bold,
        fontSize: 12,
        color: isSelected ? colors.onPrimary : colors.text,
    })
}))